import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Copy,
  ExternalLink,
  Ban,
  Shield,
  AlertTriangle,
  Zap
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWithdrawals({ token }) {
  const { t } = useTranslation();
  const [withdrawals, setWithdrawals] = useState([]);
  const [manualPayouts, setManualPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Manual payout dialog
  const [manualPayoutDialogOpen, setManualPayoutDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
    fetchStats();
    fetchManualPayouts();
  }, [statusFilter]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/referral/admin/all-withdrawals${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load withdrawals");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/admin/withdrawal-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const fetchManualPayouts = async () => {
    try {
      const response = await axios.get(`${API}/referral/admin/manual-payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManualPayouts(response.data.data || []);
    } catch (error) {
      console.error("Failed to load manual payouts:", error);
    }
  };

  const handleCompleteManualPayout = async () => {
    if (!selectedPayout || !txHash) {
      toast.error("Transaction hash is required");
      return;
    }
    
    try {
      setProcessing(true);
      await axios.put(
        `${API}/referral/admin/manual-payouts/${selectedPayout.id}/complete?tx_hash=${encodeURIComponent(txHash)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Payout marked as completed");
      setManualPayoutDialogOpen(false);
      fetchWithdrawals();
      fetchStats();
      fetchManualPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete payout");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-yellow-500/20 text-yellow-400", icon: Clock, text: "Pending" },
      processing: { color: "bg-blue-500/20 text-blue-400", icon: RefreshCw, text: "Processing" },
      completed: { color: "bg-green-500/20 text-green-400", icon: CheckCircle, text: "Completed" },
      rejected: { color: "bg-red-500/20 text-red-400", icon: XCircle, text: "Rejected" },
      failed: { color: "bg-orange-500/20 text-orange-400", icon: AlertCircle, text: "Failed" }
    };
    
    const { color, icon: Icon, text } = config[status] || config.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        {text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-400" />
            Withdrawal Management
          </h2>
          <p className="text-gray-400 text-sm mt-1 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            Automatic instant payouts - No manual approval needed!
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchWithdrawals(); fetchStats(); fetchManualPayouts(); }}
          className="border-gray-600"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Completed
            </div>
            <div className="text-2xl font-bold text-green-400">
              {stats.completed_count}
            </div>
            <div className="text-sm text-green-400/70">
              ${stats.total_paid_usdt?.toFixed(2)} USDT
            </div>
          </div>
          
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
            <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
              <Clock className="w-4 h-4" />
              Processing
            </div>
            <div className="text-2xl font-bold text-yellow-400">
              {stats.by_status?.processing?.count || 0}
            </div>
          </div>
          
          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <div className="flex items-center gap-2 text-orange-400 text-sm mb-1">
              <AlertTriangle className="w-4 h-4" />
              Manual Payouts
            </div>
            <div className="text-2xl font-bold text-orange-400">
              {manualPayouts.length}
            </div>
            <div className="text-sm text-orange-400/70">Needs attention</div>
          </div>
          
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Total Paid
            </div>
            <div className="text-2xl font-bold text-blue-400">
              ${stats.total_paid_usdt?.toFixed(2)}
            </div>
            <div className="text-sm text-blue-400/70">USDT</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="bg-gray-800/50 border border-gray-700">
          <TabsTrigger value="all">All Withdrawals</TabsTrigger>
          <TabsTrigger value="manual" className="relative">
            Manual Payouts
            {manualPayouts.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-xs flex items-center justify-center">
                {manualPayouts.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {/* Filter */}
          <div className="flex items-center gap-4 mb-4">
            <span className="text-gray-400 text-sm">Filter:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-gray-800 border-gray-700">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Withdrawals Table */}
          <div className="bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800/50">
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="px-4 py-3">Marketer</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Wallet</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">TX</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                        No withdrawals found
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((w) => (
                      <tr key={w.id} className="hover:bg-gray-800/30">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white text-sm">{w.marketer_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-400">{w.marketer_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-green-400">${w.amount?.toFixed(2)}</div>
                          <div className="text-xs text-gray-400">Net: ${w.net_amount?.toFixed(2)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-700 px-2 py-1 rounded text-yellow-400">
                              {w.wallet_address?.slice(0, 8)}...{w.wallet_address?.slice(-6)}
                            </code>
                            <button onClick={() => copyToClipboard(w.wallet_address)}>
                              <Copy className="w-3 h-3 text-gray-400 hover:text-white" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(w.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{formatDate(w.created_at)}</td>
                        <td className="px-4 py-3">
                          {w.tx_hash ? (
                            <a 
                              href={`https://tronscan.org/#/transaction/${w.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              View
                            </a>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manual" className="mt-4">
          {/* Manual Payouts Info */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-400">Manual Payouts Required</h3>
                <p className="text-sm text-orange-400/80 mt-1">
                  These payouts could not be processed automatically. Please send USDT manually and enter the transaction hash.
                </p>
              </div>
            </div>
          </div>

          {/* Manual Payouts List */}
          {manualPayouts.length === 0 ? (
            <div className="bg-gray-800/30 rounded-xl p-8 text-center border border-gray-700">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-400">No pending manual payouts</p>
              <p className="text-sm text-gray-500 mt-1">All payouts are processed automatically!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {manualPayouts.map((payout) => (
                <div 
                  key={payout.id} 
                  className="bg-gray-800/30 rounded-xl p-4 border border-gray-700 flex items-center justify-between"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-white">{payout.marketer_name}</span>
                      <span className="text-gray-400 text-sm">{payout.marketer_email}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-green-400 font-bold">${payout.amount?.toFixed(2)} USDT</span>
                      <span className="text-gray-400">→</span>
                      <code className="bg-gray-700 px-2 py-0.5 rounded text-yellow-400 text-xs">
                        {payout.wallet_address}
                      </code>
                      <button onClick={() => copyToClipboard(payout.wallet_address)}>
                        <Copy className="w-3 h-3 text-gray-400 hover:text-white" />
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setSelectedPayout(payout);
                      setTxHash("");
                      setManualPayoutDialogOpen(true);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Paid
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Manual Payout Dialog */}
      <Dialog open={manualPayoutDialogOpen} onOpenChange={setManualPayoutDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Complete Manual Payout
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayout && (
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Marketer:</span>
                  <span className="text-white">{selectedPayout.marketer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-green-400 font-bold">${selectedPayout.amount?.toFixed(2)} USDT</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-400">Wallet:</span>
                  <code className="text-yellow-400 text-xs break-all max-w-[200px]">
                    {selectedPayout.wallet_address}
                  </code>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-gray-400">
                  Transaction Hash (required)
                </label>
                <Input
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="Enter TronScan transaction hash..."
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <p className="text-xs text-gray-500">
                  After sending USDT to the wallet address, paste the transaction hash here.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setManualPayoutDialogOpen(false)}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompleteManualPayout}
              disabled={processing || !txHash}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
